// Desktop-only call popover — on mouse devices, the car page's call buttons show
// a small popover with the dealer's phone number + address instead of dialing
// straight away. On touch / mobile the buttons keep their tel: link (native
// dialer). The popover markup is rendered once server-side (listing-title.php)
// and moved to <body> so position:fixed never clips inside a transformed parent.
import { qs, qsa, on } from '../lib/dom.js';

export function initCallPopover() {
	const pop = qs('[data-call-popover]');
	const triggers = qsa('[data-call-trigger]');
	if (!pop || !triggers.length) { return; }

	if (pop.parentNode !== document.body) { document.body.appendChild(pop); }

	// Desktop = a real mouse device AND definitely not touch. The touch guard
	// matters because in-app webviews (the Facebook / Instagram / Messenger browser)
	// mis-report `hover: hover` / `pointer: fine` on touch screens — which made
	// isDesktop() true there, so the click was preventDefault()ed and the tel: link
	// never dialed. That was the whole "call buttons do nothing in the FB browser"
	// bug: tel: works fine natively in those webviews (iOS even shows an "open
	// outside Facebook?" prompt) — we just have to stop blocking it. Any ONE touch
	// signal (ontouchstart, maxTouchPoints, coarse pointer) keeps phones and every
	// in-app webview on the native-dial path; only true mouse-only devices get the
	// popover.
	const isTouch = ('ontouchstart' in window)
		|| (navigator.maxTouchPoints || 0) > 0
		|| window.matchMedia('(pointer: coarse)').matches;
	const isDesktop = () => !isTouch
		&& window.matchMedia('(hover: hover) and (pointer: fine)').matches;
	const arrow = qs('[data-callpop-arrow]', pop);
	const isOpen = () => pop.classList.contains('is-open');
	let anchor = null;
	let hideTimer = 0;

	function place() {
		if (!anchor) { return; }
		const r = anchor.getBoundingClientRect();
		const pw = pop.offsetWidth;   // measurable while display:block, even at opacity 0
		const ph = pop.offsetHeight;
		const M = 12;
		let left = r.left + r.width / 2 - pw / 2;
		left = Math.max(M, Math.min(left, window.innerWidth - pw - M));
		let top = r.bottom + 12;
		let above = false;
		if (top + ph > window.innerHeight - M && r.top - ph - 12 > M) {
			top = r.top - ph - 12;
			above = true;
		}
		pop.style.left = Math.round(left) + 'px';
		pop.style.top = Math.round(top) + 'px';
		pop.classList.toggle('is-above', above);
		if (arrow) { arrow.style.left = Math.round(r.left + r.width / 2 - left) + 'px'; }
	}

	function open(btn) {
		clearTimeout(hideTimer);
		anchor = btn;
		// A trigger inside an OPEN <dialog> (the gallery lightbox) lives in the
		// browser top layer — a body-level popover would render behind it. Re-parent
		// into that dialog so the popover joins the top layer; position:fixed still
		// resolves against the viewport, so place() stays correct. Non-dialog
		// triggers keep the popover on <body>.
		const dlg = btn.closest('dialog');
		const host = (dlg && dlg.open) ? dlg : document.body;
		if (pop.parentNode !== host) { host.appendChild(pop); }
		pop.hidden = false;           // display:block, opacity 0 (pre-transition)
		place();
		requestAnimationFrame(() => pop.classList.add('is-open'));
	}
	function close() {
		pop.classList.remove('is-open');
		anchor = null;
		hideTimer = setTimeout(() => { if (!isOpen()) { pop.hidden = true; } }, 200);
	}

	triggers.forEach((btn) => {
		on(btn, 'click', (e) => {
			if (!isDesktop()) { return; }       // mobile → let the tel: link dial
			e.preventDefault();
			if (isOpen() && anchor === btn) { close(); return; }
			open(btn);
		});
	});

	on(document, 'click', (e) => {
		if (isOpen() && !pop.contains(e.target) && !e.target.closest('[data-call-trigger]')) { close(); }
	});
	on(document, 'keydown', (e) => { if (e.key === 'Escape' && isOpen()) { close(); } });
	// If the popover was opened from inside a lightbox <dialog>, closing that
	// dialog must also dismiss the popover (it was re-parented into it).
	qsa('dialog').forEach((d) => on(d, 'close', () => { if (isOpen()) { close(); } }));
	on(window, 'scroll', () => { if (isOpen()) { place(); } }, { passive: true });
	on(window, 'resize', () => { if (isOpen()) { place(); } });
	const closeBtn = qs('[data-callpop-close]', pop);
	if (closeBtn) { on(closeBtn, 'click', close); }
}
